/**
 * Stat tile body (S4) — a big number with severity coloring driven by
 * `thresholdEval` (thresholds are user-declared bands, e.g. `>= 0.4 warning`),
 * an optional previous-period/target compare chip, and an optional sparkline
 * (rendered ONLY when the tile explicitly declares `sparkline.timeColumn` +
 * `valueColumn` — never inferred, per S1 contract). Shares `useTileResult` +
 * `QueryTileStates` with the chart tile (S3) so refresh stays flicker-free
 * and the four designed states (empty/loading/error/success) stay identical
 * across tile types. Also owns the S12 alert badge — a muted bell when the
 * tile has an `alertRule` and is armed, a red ringing bell while firing.
 */
import { Bell, BellRing, Minus, TrendingDown, TrendingUp } from 'lucide-react';
import type { StatTile } from '../../types/dashboardTypes';
import { useTileResult } from '../../hooks/useTileResult';
import { useTileAlert, type TileAlertBadgeStatus } from '../../hooks/useTileAlert';
import { analyzeColumns } from '../../../charts/utils/columnTypeDetection';
import {
  computeStatCompare,
  resolveStatTileRefs,
  resolveStatValue,
  type StatCompareOutcome,
} from '../../utils/statFormat';
import { thresholdEval } from '../../utils/thresholdEval';
import { QueryTileStates } from './QueryTileStates';
import { StatSparkline } from './StatSparkline';
import { buildSparklinePoints, SEVERITY_TEXT } from './statTileData';
import { tileEmptyText } from './tileEmptyText';

const DIRECTION_ICON = { up: TrendingUp, down: TrendingDown, flat: Minus } as const;

/** Muted "armed" bell when a rule exists but hasn't crossed; a ringing red
 * bell while firing. Absent entirely when the tile has no `alertRule`. */
function AlertBadge({ status, rule }: { status: TileAlertBadgeStatus; rule: StatTile['alertRule'] }) {
  if (!rule || !status) return null;
  if (status === 'firing') {
    return (
      <span
        className="inline-flex shrink-0 items-center text-red-500"
        title={`Alert firing — value is ${rule.operator} ${rule.threshold}`}
        data-testid="stat-tile-alert-badge"
        data-status="firing"
      >
        <BellRing className="h-3.5 w-3.5" aria-hidden />
        <span className="sr-only">Alert firing</span>
      </span>
    );
  }
  return (
    <span
      className="inline-flex shrink-0 items-center text-muted-foreground"
      title={`Alert armed — notifies when value is ${rule.operator} ${rule.threshold}`}
      data-testid="stat-tile-alert-badge"
      data-status="armed"
    >
      <Bell className="h-3.5 w-3.5" aria-hidden />
      <span className="sr-only">Alert armed</span>
    </span>
  );
}

/** Exported for the add-tile dialog's live stat preview (visual parity). */
export function CompareChip({ compare }: { compare: StatCompareOutcome }) {
  if (compare.kind === 'none') return null;
  if (compare.kind === 'error') {
    return (
      <span className="text-[11px] text-muted-foreground" data-testid="stat-tile-compare-error">
        Compare unavailable — {compare.error.kind === 'missing-column' ? 'column not found' : 'not numeric'}
      </span>
    );
  }
  const Icon = DIRECTION_ICON[compare.direction];
  return (
    <span
      className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground"
      data-testid="stat-tile-compare-chip"
      data-direction={compare.direction}
    >
      <Icon className="h-3 w-3" aria-hidden />
      {compare.text}
    </span>
  );
}

export function StatTileView({ dashboardId, tile }: { dashboardId: string; tile: StatTile }) {
  const state = useTileResult(dashboardId, tile);
  const selected = state.result?.selected;
  const row = selected?.rows[0];
  // Resolves `valueColumn`/`compare.column`/`sparkline.*` against the
  // CURRENT result columns — the same seam `alertValuePreview` and the HTML
  // snapshot's `buildStatTileHtml` use, so an older tile's NAME-based (or
  // whitespace-rekeyed) reference reads the right column everywhere instead
  // of just here (PR review, P2).
  const columnInfos = analyzeColumns(selected?.columns ?? [], selected?.rows ?? []);
  const resolvedRefs = resolveStatTileRefs(tile, columnInfos);
  const valueColumn = resolvedRefs.valueColumn;

  const outcome = valueColumn ? resolveStatValue(row, valueColumn, tile.format) : undefined;
  const severity = outcome?.ok ? thresholdEval(outcome.raw, tile.thresholds) : 'none';
  const compare = outcome?.ok ? computeStatCompare(outcome.raw, resolvedRefs.compare, row) : undefined;
  const sparklinePoints = buildSparklinePoints(selected?.rows, resolvedRefs.sparkline);
  const alertStatus = useTileAlert(dashboardId, tile, outcome?.ok ? outcome.raw : undefined);

  return (
    <QueryTileStates state={state}>
      {!row || !valueColumn ? (
        <div
          className="flex h-full items-center justify-center px-3 text-center text-xs text-muted-foreground"
          data-testid="stat-tile-empty"
        >
          {!row ? tileEmptyText(tile, 'No data') : 'No numeric column configured for this query'}
        </div>
      ) : outcome && !outcome.ok ? (
        <div
          className="flex h-full items-center justify-center px-3 text-center text-xs text-muted-foreground"
          data-testid="stat-tile-config-error"
        >
          {outcome.error.kind === 'missing-column'
            ? `Column "${outcome.error.column}" was not found in the result`
            : `Column "${outcome.error.column}" is not numeric`}
        </div>
      ) : outcome ? (
        // Centered, container-scaled hero number: a stat tile is read at a
        // glance (often on a wall display), so the value owns the middle of
        // the tile instead of hugging the left edge, and grows with the tile
        // rather than staying 24px in a half-metre-wide card. The sparkline
        // stays glued to the value as ONE centered group, so the arrangement
        // reads the same with or without it.
        <div
          className="@container flex h-full flex-col items-center justify-center gap-1.5 px-4 py-2 text-center"
          data-testid="stat-tile-body"
        >
          <div className="flex max-w-full items-end justify-center gap-2">
            <span className="flex min-w-0 items-center gap-1.5">
              <span
                className={`max-w-full truncate text-2xl leading-tight font-semibold tabular-nums @2xs:text-3xl @md:text-4xl @2xl:text-5xl ${SEVERITY_TEXT[severity]}`}
                data-testid="stat-tile-value"
                data-severity={severity}
                title={outcome.text}
              >
                {outcome.text}
              </span>
              <AlertBadge status={alertStatus} rule={tile.alertRule} />
            </span>
            {sparklinePoints.length >= 2 && <StatSparkline points={sparklinePoints} severity={severity} />}
          </div>
          {compare && <CompareChip compare={compare} />}
        </div>
      ) : null}
    </QueryTileStates>
  );
}
